import React, { useCallback, useEffect, useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import Tag from '@atlaskit/tag';
import TagGroup from '@atlaskit/tag-group';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import type { UpdateWorkItemAllFieldTypesAction } from '../../../../../common/types';
import { FieldTooltipContent } from '../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../common/utils/useRegisterTooltipContentProvider';
import { getActionData } from '../../../utils';

import type { labelsField_updateWorkItemAllFieldTypesActionRenderer$key } from './__generated__/labelsField_updateWorkItemAllFieldTypesActionRenderer.graphql';
import { parseLabels } from './parseLabels';

const messages = defineMessages({
	suggestedLabelsToAdd: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.labels-field.suggestedLabelsToAdd.non-final',
		defaultMessage: 'Suggested labels to add',
		description: 'Label for the suggested labels to add in the labels field',
	},
	suggestedLabelsToRemove: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.labels-field.suggestedLabelsToRemove.non-final',
		defaultMessage: 'Suggested labels to remove',
		description: 'Label for the suggested labels to remove in the labels field',
	},
	updateLabels: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.labels-field.updateLabels.non-final',
		defaultMessage: 'Update labels',
		description: 'Label for the update labels in the labels field',
	},
	addLabels: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.labels-field.addLabels.non-final',
		defaultMessage: 'Add labels',
		description: 'Label for adding labels in the labels field',
	},
});

const getDefinedLabels = (labels: ReadonlyArray<string | null | undefined>): string[] =>
	labels.filter((label): label is string => label !== undefined && label !== null);

export type Props = {
	queryRef: labelsField_updateWorkItemAllFieldTypesActionRenderer$key;
	action: UpdateWorkItemAllFieldTypesAction;
	registerTooltipProvider?: RegisterTooltipProvider;
};

export const LabelsField = ({
	queryRef,
	action,
	registerTooltipProvider,
}: Props): React.JSX.Element | null => {
	di(useAnalytics);

	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const { fieldKey, fieldValue, fieldReasoning } = getActionData(action);

	const data = useFragment(
		graphql`
			fragment labelsField_updateWorkItemAllFieldTypesActionRenderer on JiraLabelsField {
				fieldId
				name
				selectedLabelsConnection {
					edges {
						node {
							name
						}
					}
				}
			}
		`,
		queryRef,
	);

	const isFieldRenderable = data?.fieldId === fieldKey && fieldKey === 'labels';

	const suggestedLabels = useMemo(() => parseLabels(fieldValue), [fieldValue]);

	const currentLabels = useMemo(
		() => data?.selectedLabelsConnection?.edges?.map((edge) => edge?.node?.name) ?? [],
		[data?.selectedLabelsConnection],
	);

	const addedLabels = useMemo(
		() => suggestedLabels?.filter((label) => !currentLabels?.includes(label ?? '')) ?? [],
		[suggestedLabels, currentLabels],
	);
	const removedLabels = useMemo(
		() => currentLabels.filter((label) => !suggestedLabels?.includes(label ?? '')),
		[currentLabels, suggestedLabels],
	);

	const isSameValueSuggested =
		isFieldRenderable &&
		action.status === 'planned' &&
		suggestedLabels !== null &&
		addedLabels.length === 0 &&
		removedLabels.length === 0;

	useEffect(() => {
		if (isSameValueSuggested && fg('jira-track-invalid-update-workitem-actions')) {
			sendAnalyticsEvent({
				eventType: 'operational',
				action: 'detected',
				actionSubject: 'invalidField',
				actionSubjectId: 'updateWorkActionRenderer',
				attributes: {
					fieldType: 'labels',
					anomaly: 'sameValueSuggested',
					invocationId: action.invocationId,
				},
			});
		}
	}, [isSameValueSuggested, action.invocationId, sendAnalyticsEvent]);

	const toolTipContentProvider = useCallback(() => {
		const currentLabels =
			data?.selectedLabelsConnection?.edges?.map((edge) => edge?.node?.name) ?? [];

		const current = currentLabels.length ? (
			<TagGroup>
				{currentLabels
					.filter((label) => label !== undefined && label !== null)
					.map((label) => (
						<Tag key={label} text={label} isRemovable={false} />
					))}
			</TagGroup>
		) : null;

		const suggestedToAdd = addedLabels?.length ? (
			<TagGroup>
				{addedLabels
					.filter((label) => label !== undefined && label !== null)
					.map((label) => (
						<Tag key={label} text={label} isRemovable={false} />
					))}
			</TagGroup>
		) : null;

		const suggestedToRemove = removedLabels?.length ? (
			<TagGroup>
				{removedLabels
					.filter((label) => label !== undefined && label !== null)
					.map((label) => (
						<Tag key={label} text={label} isRemovable={false} />
					))}
			</TagGroup>
		) : null;

		return (
			<FieldTooltipContent
				reasoning={fieldReasoning}
				current={current}
				additionalSuggestions={[
					{
						suggested: addedLabels.length > 0 ? suggestedToAdd : null,
						label: formatMessage(messages.suggestedLabelsToAdd),
						testIdSuffix: 'suggested-labels-to-add',
					},
					{
						suggested: removedLabels.length > 0 ? suggestedToRemove : null,
						label: formatMessage(messages.suggestedLabelsToRemove),
						testIdSuffix: 'suggested-labels-to-remove',
					},
				]}
				fieldName={data?.name}
			/>
		);
	}, [
		fieldReasoning,
		data?.name,
		data?.selectedLabelsConnection?.edges,
		formatMessage,
		addedLabels,
		removedLabels,
	]);

	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: fieldKey,
		provider: toolTipContentProvider,
		enabled: isFieldRenderable,
	});

	const inlineAddedLabels = useMemo(() => getDefinedLabels(addedLabels), [addedLabels]);
	const inlineRemovedLabels = useMemo(() => getDefinedLabels(removedLabels), [removedLabels]);

	if (!isFieldRenderable) {
		return null;
	}

	if (
		inlineAddedLabels.length > 0 &&
		inlineRemovedLabels.length === 0 &&
		FeatureGates.getExperimentValue(
			'jira-ai-update-workitem-field-action-details-exp',
			'isEnabled',
			false,
		)
	) {
		return (
			<Inline
				alignBlock="center"
				space="space.050"
				testId="update-work-item-all-fields-action-renderer.ui.action-card.card-fields-list.labels-field"
			>
				<Text color="color.text.subtle" maxLines={1}>
					{formatMessage(messages.addLabels)}
				</Text>
				{inlineAddedLabels.map((label) => (
					<Tag key={label} text={label} isRemovable={false} hasMargin={false} />
				))}
			</Inline>
		);
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-all-fields-action-renderer.ui.action-card.card-fields-list.labels-field"
		>
			{formatMessage(messages.updateLabels)}
		</Text>
	);
};
